import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, Plus, Search, X } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useUserIdentities, useUserProfile } from "@/hooks/useUserProfile";
import { ownedRecipientOptions } from "@/lib/ownedRecipientOptions";
import { normalizeOwnedDomains } from "@shared/ownedDomains";

type RecipientSource = "identity" | "contact" | "company" | "company_channel" | "lead";

interface RecipientOption {
  email: string;
  label: string;
  detail: string;
  source: RecipientSource;
  entityId: number;
}

interface CrmRecipientPickerProps {
  id: string;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  knownEmails?: string[];
  placeholder?: string;
  disabled?: boolean;
  onAddMissingContact?: (email: string) => void;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const normalizeEmail = (email: string) => email.trim().replace(/[,;]+$/, "").toLowerCase();
const emailDomain = (email: string) => normalizeEmail(email).split("@")[1] || "";

function isOwnedDomain(domain: string, ownedDomains: ReadonlySet<string>): boolean {
  return Array.from(ownedDomains).some((owned) => domain === owned || domain.endsWith(`.${owned}`));
}

function titleCase(value: string) {
  return value
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function channelEmail(channel: string): string | null {
  const match = channel.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match ? normalizeEmail(match[0]) : null;
}

function channelName(channel: string, email: string): string {
  const bracketIndex = channel.indexOf("<");
  const explicitName = bracketIndex > 0 ? channel.slice(0, bracketIndex).trim() : "";
  if (explicitName) return explicitName;
  return titleCase(email.split("@")[0] || "Canale");
}

function listFromResponse(response: unknown): any[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === "object" && Array.isArray((response as any).data)) return (response as any).data;
  if (response && typeof response === "object" && Array.isArray((response as any).items)) {
    return (response as any).items.map((item: any) => item.record).filter(Boolean);
  }
  return [];
}

function pushOption(options: RecipientOption[], seen: Set<string>, option: RecipientOption) {
  const normalized = normalizeEmail(option.email);
  if (!normalized || seen.has(normalized) || !isValidEmail(normalized)) return;
  seen.add(normalized);
  options.push({ ...option, email: normalized });
}

function recipientOptionsFromCrm(contacts: any[], companies: any[], leads: any[]): RecipientOption[] {
  const options: RecipientOption[] = [];
  const seen = new Set<string>();

  contacts.forEach((contact) => {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || "Contatto";
    [contact.companyEmail, contact.privateEmail, contact.email].filter(Boolean).forEach((email) => {
      const domain = emailDomain(email);
      pushOption(options, seen, {
        email,
        label: domain ? `${name} · ${domain}` : name,
        detail: contact.companyName ? `Contatto · ${contact.companyName}` : "Contatto CRM",
        source: "contact",
        entityId: contact.id,
      });
    });
  });

  companies.forEach((company) => {
    if (company.email) {
      const domain = emailDomain(company.email);
      pushOption(options, seen, {
        email: company.email,
        label: domain ? `${company.name || "Azienda"} · ${domain}` : company.name || "Azienda",
        detail: "Email azienda",
        source: "company",
        entityId: company.id,
      });
    }

    const channels = Array.isArray(company.channels) ? company.channels : [];
    channels.forEach((channel: string) => {
      const email = channelEmail(channel);
      if (!email) return;
      const domain = emailDomain(email);
      pushOption(options, seen, {
        email,
        label: `${channelName(channel, email)} · ${domain}`,
        detail: company.name ? `Canale aziendale · ${company.name}` : "Canale aziendale",
        source: "company_channel",
        entityId: company.id,
      });
    });
  });

  leads.forEach((lead) => {
    const name = [lead.firstName, lead.lastName].filter(Boolean).join(" ").trim() || lead.company || "Lead";
    [lead.companyEmail, lead.privateEmail, lead.email].filter(Boolean).forEach((email) => {
      const domain = emailDomain(email);
      pushOption(options, seen, {
        email,
        label: `${name} · lead`,
        detail: domain ? `Lead · ${domain}` : "Lead CRM",
        source: "lead",
        entityId: lead.id,
      });
    });
  });

  return options;
}

export function CrmRecipientPicker({
  id,
  label,
  value,
  onChange,
  knownEmails = [],
  placeholder = "Cerca nel CRM o inserisci email...",
  disabled = false,
  onAddMissingContact,
}: CrmRecipientPickerProps) {
  const [inputValue, setInputValue] = useState("");
  const [resolvedEmails, setResolvedEmails] = useState<Set<string>>(() => new Set());
  const [selectedOptions, setSelectedOptions] = useState<Record<string, RecipientOption>>({});
  const { data: userProfile } = useUserProfile();
  const { data: userIdentities = [] } = useUserIdentities();
  const search = inputValue.trim();
  // Due caratteri evitano scansioni rumorose; i tre moduli riusano lo stesso
  // contratto smart-search di liste e autocomplete (ordine libero, accenti e
  // refusi ragionevoli), senza tre implementazioni locali divergenti.
  const shouldSearch = search.length >= 2;
  const recipientEmails = useMemo(
    () => [...new Set(value.map(normalizeEmail).filter(isValidEmail))],
    [value],
  );

  const { data: contactsResponse, isFetching: isFetchingContacts, isError: isErrorContacts } = useQuery({
    queryKey: ["/api/contacts", "email-recipient-picker", search],
    enabled: shouldSearch,
    queryFn: () => apiRequest('/api/crm-query/execute', { method: 'POST', isMutation: false, data: { entityType: 'contact', search, filters: [], sorts: [], limit: 8, offset: 0 } }),
  });

  const { data: companiesResponse, isFetching: isFetchingCompanies, isError: isErrorCompanies } = useQuery({
    queryKey: ["/api/companies", "email-recipient-picker", search],
    enabled: shouldSearch,
    queryFn: () => apiRequest('/api/crm-query/execute', { method: 'POST', isMutation: false, data: { entityType: 'company', search, filters: [], sorts: [], limit: 8, offset: 0 } }),
  });

  const { data: leadsResponse, isFetching: isFetchingLeads, isError: isErrorLeads } = useQuery({
    queryKey: ["/api/leads", "email-recipient-picker", search],
    enabled: shouldSearch,
    queryFn: () => apiRequest('/api/crm-query/execute', { method: 'POST', isMutation: false, data: { entityType: 'lead', search, filters: [], sorts: [], limit: 8, offset: 0 } }),
  });

  // La ricerca testuale e' solo un aiuto di selezione. La decisione
  // "questo indirizzo e' gia' un contatto?" deve arrivare dal resolver
  // canonico, altrimenti una pagina limitata o una mail storica produce il
  // falso invito "Aggiungi contatto".
  const {
    data: resolvedContactsResponse,
    isFetching: isResolvingContacts,
    isError: isContactResolutionError,
  } = useQuery({
    queryKey: ["/api/contacts/resolve-emails", recipientEmails.join(",")],
    enabled: recipientEmails.length > 0,
    queryFn: () => apiRequest(`/api/contacts/resolve-emails?${recipientEmails.map((email) => `email=${encodeURIComponent(email)}`).join("&")}`),
  });

  const options = useMemo(() => {
    const own = ownedRecipientOptions(userProfile, userIdentities, search);
    const ownEmails = new Set(own.map(option => option.email));
    return [...own, ...recipientOptionsFromCrm(
      listFromResponse(contactsResponse),
      listFromResponse(companiesResponse),
      listFromResponse(leadsResponse),
    ).filter(option => !ownEmails.has(option.email))];
  }, [companiesResponse, contactsResponse, leadsResponse, userProfile, userIdentities, search]);

  const resolvedContactEmails = useMemo(() => new Set<string>(
    Array.isArray((resolvedContactsResponse as any)?.resolved)
      ? (resolvedContactsResponse as any).resolved.map((row: any) => normalizeEmail(String(row.email || "")))
      : [],
  ), [resolvedContactsResponse]);
  const resolvedLeadEmails = useMemo(() => new Set<string>(
    Array.isArray((resolvedContactsResponse as any)?.leadCandidates)
      ? (resolvedContactsResponse as any).leadCandidates.map((row: any) => normalizeEmail(String(row.email || "")))
      : [],
  ), [resolvedContactsResponse]);
  // Il picker e' usato da A, CC e CCN: non puo' delegare questa decisione al
  // solo post-ingest. Qualunque identita' o dominio posseduto configurato
  // appartiene al proprietario CRM e non deve mai essere proposto come nuovo
  // Contatto/Lead, anche se non esiste una scheda persona separata.
  const internalEmailSet = useMemo(() => new Set([
    userProfile?.email,
    userProfile?.privateEmail,
    userProfile?.backupEmail,
    ...userIdentities.map((identity) => identity.email),
  ].map((email) => normalizeEmail(String(email ?? ''))).filter(Boolean)), [userIdentities, userProfile]);
  const ownedDomainSet = useMemo(() => new Set(normalizeOwnedDomains(userProfile?.ownedDomains ?? [])), [userProfile?.ownedDomains]);
  const isInternalRecipient = (email: string) => {
    const normalized = normalizeEmail(email);
    return internalEmailSet.has(normalized) || isOwnedDomain(emailDomain(normalized), ownedDomainSet);
  };

  const knownEmailSet = useMemo(() => {
    const known = new Set(resolvedEmails);
    resolvedContactEmails.forEach((email) => known.add(email));
    resolvedLeadEmails.forEach((email) => known.add(email));
    internalEmailSet.forEach((email) => known.add(email));
    knownEmails.forEach((email) => {
      const normalized = normalizeEmail(email);
      if (normalized) known.add(normalized);
    });
    options.forEach((option) => known.add(option.email));
    return known;
  }, [internalEmailSet, knownEmails, options, resolvedEmails, resolvedContactEmails, resolvedLeadEmails]);
  const resolutionReady = recipientEmails.length === 0
    || (!isResolvingContacts && !isContactResolutionError && Boolean(resolvedContactsResponse));
  const isFetching = isFetchingContacts || isFetchingCompanies || isFetchingLeads;
  const searchFailed = isErrorContacts || isErrorCompanies || isErrorLeads;
  const normalizedInput = normalizeEmail(inputValue);
  const canAddManual = isValidEmail(normalizedInput) && !value.map(normalizeEmail).includes(normalizedInput);

  const addRecipient = (email: string, option?: RecipientOption) => {
    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized) || value.some((item) => normalizeEmail(item) === normalized)) {
      setInputValue("");
      return;
    }
    if (option) {
      setResolvedEmails((current) => {
        const next = new Set(current);
        next.add(normalized);
        return next;
      });
      setSelectedOptions((current) => ({ ...current, [normalized]: option }));
    }
    onChange([...value, normalized]);
    setInputValue("");
  };

  const removeRecipient = (email: string) => {
    onChange(value.filter((item) => item !== email));
    setResolvedEmails((current) => {
      const next = new Set(current);
      next.delete(email);
      return next;
    });
    setSelectedOptions((current) => {
      const next = { ...current };
      delete next[email];
      return next;
    });
  };

  // Un Lead esistente e' gia' un'identita' CRM: non si deve proporre la
  // creazione di un secondo Contatto. La conversione avviene dalla scheda
  // Lead/endpoint di conversione, mentre il picker resta coerente e non
  // fabbrica duplicati.
  const missingRecipients = value.filter((email) => {
    const normalized = normalizeEmail(email);
    return !isInternalRecipient(normalized) && !knownEmailSet.has(normalized) && !resolvedLeadEmails.has(normalized);
  });

  return (
    <div className="space-y-2">
      {label ? <label htmlFor={id} className="text-xs font-medium text-foreground">{label}</label> : null}
      <div className="border border-border bg-background p-2">
        <div className="flex flex-wrap gap-1.5">
          {value.map((email) => {
            const normalizedEmail = normalizeEmail(email);
            const option = options.find((item) => item.email === normalizedEmail) ?? selectedOptions[normalizedEmail];
            const isKnownEmail = knownEmailSet.has(normalizedEmail);
            const isInternal = isInternalRecipient(normalizedEmail);
            return (
              <span key={email} className="inline-flex max-w-full items-center gap-1 border border-border bg-muted px-2 py-1 text-xs text-foreground">
                <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">{option ? `${option.label} <${email}>` : email}</span>
                {isInternal ? <span className="shrink-0 text-xs font-medium text-muted-foreground">identità interna</span> : resolutionReady && !isKnownEmail ? <span className="shrink-0 text-xs font-medium text-muted-foreground">non nel CRM</span> : null}
                <button type="button" onClick={() => removeRecipient(email)} disabled={disabled} aria-label={`Rimuovi ${email}`} className="text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <div className="flex min-w-[180px] flex-1 items-center gap-1">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              id={id}
              type="text"
              value={inputValue}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(event) => setInputValue(event.target.value)}
              onBlur={() => {
                if (canAddManual) {
                  const exactOption = options.find((option) => option.email === normalizedInput);
                  addRecipient(exactOption?.email ?? normalizedInput, exactOption);
                }
              }}
              onKeyDown={(event) => {
                if (["Enter", "Tab", ",", ";"].includes(event.key) && inputValue.trim()) {
                  event.preventDefault();
                  const exactOption = options.find((option) => option.email === normalizedInput);
                  addRecipient(exactOption?.email ?? normalizedInput, exactOption);
                }
                if (event.key === "Backspace" && !inputValue && value.length > 0) {
                  event.preventDefault();
                  onChange(value.slice(0, -1));
                }
              }}
              className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </div>

      {shouldSearch && (
        <div className="border border-border bg-background">
          {isFetching && options.length === 0 && !canAddManual ? (
            <div className="px-2 py-1.5 text-xs font-normal text-muted-foreground">Ricerca destinatari nel CRM...</div>
          ) : options.length > 0 || canAddManual ? (
            <div className="max-h-40 overflow-y-auto">
              {canAddManual && (
                <button
                  type="button"
                  onClick={() => addRecipient(normalizedInput)}
                  className="flex w-full min-w-0 items-center justify-between gap-2 border-b border-border px-2 py-1.5 text-left text-xs hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">{normalizedInput}</span>
                    <span className="block truncate text-xs font-normal text-muted-foreground">Indirizzo libero</span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">Email libera</span>
                </button>
              )}
              {options.map((option) => (
                <button
                  key={`${option.source}-${option.entityId}-${option.email}`}
                  type="button"
                  onClick={() => addRecipient(option.email, option)}
                  className="flex w-full min-w-0 items-center justify-between gap-2 border-b border-border px-2 py-1.5 text-left text-xs last:border-b-0 hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">{option.label}</span>
                    <span className="block truncate text-xs font-normal text-muted-foreground">{option.email}</span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">{option.detail}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-1 px-2 py-1.5 text-xs font-normal text-muted-foreground">
              <div>{searchFailed ? "Ricerca destinatari non disponibile. Riprova tra poco." : "Nessun destinatario trovato nel CRM."}</div>
              {canAddManual && <div>Premi Invio per usare {normalizedInput} come indirizzo manuale.</div>}
            </div>
          )}
          {options.length > 0 && isFetching && <p role="status" className="px-2 py-1.5 text-xs text-muted-foreground">Altri risultati in arrivo...</p>}
          {options.length > 0 && searchFailed && <p role="status" className="px-2 py-1.5 text-xs text-muted-foreground">Ricerca parziale: alcuni risultati non sono disponibili.</p>}
        </div>
      )}

      {isResolvingContacts && recipientEmails.length > 0 && (
        <div className="border border-border bg-muted p-2 text-xs font-normal text-muted-foreground">
          Verifico gli indirizzi nei contatti CRM...
        </div>
      )}

      {!isResolvingContacts && isContactResolutionError && recipientEmails.length > 0 && (
        <div className="border border-destructive/30 bg-destructive/5 p-2 text-xs font-normal text-destructive">
          Non riesco a verificare gli indirizzi nei contatti CRM: l&apos;aggiunta automatica e&apos; bloccata finche&apos; la verifica non riesce.
        </div>
      )}

      {!isResolvingContacts && !isContactResolutionError && missingRecipients.length > 0 && (
        <div className="space-y-1 border border-primary/30 bg-primary/5 p-2 text-xs font-normal text-muted-foreground">
          {missingRecipients.map((email) => (
            <div key={email} className="flex flex-wrap items-center gap-1.5">
              <span>Vuoi aggiungere {email} tra i contatti?</span>
              <button
                type="button"
                onClick={() => onAddMissingContact?.(email)}
                className="border border-primary bg-background px-1.5 py-0.5 text-xs font-medium text-primary hover:bg-muted"
              >
                Aggiungi contatto
              </button>
            </div>
          ))}
        </div>
      )}
      {!isResolvingContacts && !isContactResolutionError && resolvedLeadEmails.size > 0 && (
        <div className="border border-primary/30 bg-primary/5 p-2 text-xs font-normal text-muted-foreground">
          {Array.from(resolvedLeadEmails).map((email) => (
            <div key={email}>Lead già presente per {email}: apri il Lead e usa “Converti in Contatto”.</div>
          ))}
        </div>
      )}
    </div>
  );
}
